import React, { createContext, useContext, useEffect } from 'react'
import useReducerWithCallback from './useReducerWithCallback'
import crypto from 'crypto'
import {
  PAGE_URL,
  SCHEDULING_PAY_STATUS,
  formatCreditCard,
  validatePaymentData
} from 'src/utils'
import MODAL from '../utils/modals'

import {
  pay,
  changePaymentMethod,
  purchaseAndPayPackage
} from '../services/schedulingService'
import { shouldResetScheduling } from '../utils/scheduling'

const CREDIT_CARD_PUBLIC_KEY = process.env.CREDIT_CARD_PUBLIC_KEY

export const PaymentContext = createContext()

const INITIAL_PAYMENT_STATE = {
  schedulingList: [],
  packageData: null,
  purchaseType: 'scheduling',
  address: undefined,
  selectedCard: undefined,
  modal: undefined,
  acceptTerms: false
}

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.UPDATE_STATE:
      return { ...state, ...action.payload }
    case actionTypes.RESET_STATE:
      return INITIAL_PAYMENT_STATE
    default:
      return state
  }
}

export const actionTypes = {
  UPDATE_STATE: 'UPDATE_STATE',
  RESET_STATE: 'RESET_STATE'
}

const saveStateToLocalStorage = (state) => {
  try {
    const serializedState = JSON.stringify(state)
    window.localStorage.setItem('paymentState', serializedState) // eslint-disable-line
  } catch (err) {
    console.error('Erro ao salvar no localStorage:', err)
  }
}

const loadStateFromLocalStorage = () => {
  try {
    if (shouldResetScheduling()) {
      window.localStorage.removeItem('paymentState')
      return undefined
    }
    const serializedState = window.localStorage.getItem('paymentState') // eslint-disable-line
    return typeof serializedState !== 'undefined' ? JSON.parse(serializedState) : undefined
  } catch {
    return undefined
  }
}

export function PaymentContextProvider ({ children }) {
  const initialState = loadStateFromLocalStorage() || INITIAL_PAYMENT_STATE
  const [state, dispatch] = useReducerWithCallback(reducer, initialState)

  useEffect(() => {
    saveStateToLocalStorage(state)
  }, [state])

  const confirmAddress = (address) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        address
      }
    })
  }

  const updateSchedulings = (schedulingList) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        schedulingList,
        purchaseType: 'scheduling',
        packageData: null
      }
    })
  }

  const updateAcceptTerms = (acceptTerms) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        acceptTerms
      }
    })
  }

  const updatePackageData = (packageData) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        packageData,
        purchaseType: 'package',
        schedulingList: [],
        acceptTerms: false
      }
    })
  }

  const updatePurchaseType = (purchaseType) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        purchaseType
      }
    })
  }

  const storeCreditCard = (props) => {
    const { values, newCard, router } = props
    let encryptCard
    let payload

    if (newCard) {
      encryptCard = encryptCreditCard(values)
      payload = {
        encryptCard,
        brand: values.brand,
        schedulings: values.schedulings,
        installments: values.installments,
        lastDigits: values.cardNumber.substring(
          values.cardNumber.length - 4,
          values.cardNumber.length
        ),
        storeCard: values.storeCard
      }
    } else {
      payload = {
        cardHash: values.hash,
        brand: values.brand,
        lastDigits: values.lastDigits,
        schedulings: values.schedulings,
        installments: values.installments
      }
    }

    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        card: {
          ...payload
        }
      }
    })

    router.push({
      pathname: PAGE_URL.CHECKOUT_PAYMENT_REVIEW,
      query: router.query
    })
  }

  const encryptCreditCard = (card) => {
    const { schedulings, ...cardData } = card
    const buffer = Buffer.from(JSON.stringify(cardData), 'utf8')
    const creditCardPublicKey = Buffer.from(CREDIT_CARD_PUBLIC_KEY, 'base64').toString('utf-8')
    const encrypted = crypto.publicEncrypt(creditCardPublicKey.replace(/\\n/g, '\n'), buffer)
    return encrypted.toString('base64')
  }

  // fecha modal de erro
  const handleModalClose = () => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        modal: undefined
      }
    })
  }

  const showPackageCancelModal = (router) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      isServicePackage,
      payload: {
        modal: MODAL.PACKAGE_CANCEL({
          onConfirm: () => {
            handleModalClose()
          },
          onCancel: () => {
            router.push(PAGE_URL.SCHEDULING_INDEX + '?cancelledPackage=true')
            handleModalClose()
          },
          onClose: () => {
            handleModalClose()
          }
        })
      }
    })
  }

  // retorna modal de erro
  const showSchedulingFailureModal = ({ id, multi, router, isServicePackage, showLoader, hideLoader }) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        modal: MODAL.PAYMENT_PROBLEM({
          onCancel: async () => {
            showLoader()
            handleModalClose()
            await handleSetLocalPayment({ id, multi, router })
            hideLoader()
          },
          onConfirm: () => {
            handleReviewInformation({ router })
            handleModalClose()
          },
          onClose: () => {
            handleModalClose()
          },
          isServicePackage
        })
      }
    })
  }

  const handleReviewInformation = ({ router }) => {
    router.push(PAGE_URL.CHECKOUT_CARD)
  }

  const handleSetLocalPayment = async ({ id, multi, router }) => {
    try {
      await changePaymentMethod(id)
      handlePushToResume({ id, multi, router })
    } catch (error) {
      console.log(error)
    }
  }

  const showSucessModal = ({ id, multi, router, isServicePackage }) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      payload: {
        modal: MODAL.PAYMENT_SUCCESS({
          onCancel: () => {
            if (isServicePackage) {
              window.localStorage.removeItem('schedulingState')
              window.localStorage.removeItem('paymentState')
              router.push(PAGE_URL.SCHEDULING_PET_AND_SERVICES)
              handleModalClose()
              return
            }
            handlePushToResume({ id, multi, router })
            handleModalClose()
          },
          onConfirm: () => {
            router.push(PAGE_URL.SCHEDULING_INDEX + '?successPackage=true')
            handleModalClose()
          },
          isServicePackage
        })
      }
    })
  }

  const showPaymentProcessingModal = ({ router, isServicePackage }) => {
    dispatch({
      type: actionTypes.UPDATE_STATE,
      isServicePackage,
      payload: {
        modal: MODAL.PAYMENT_PROCESSING({
          onConfirm: () => {
            handlePushToSchedulings({ router })
            handleModalClose()
          }
        })
      }
    })
  }

  const handlePushToResume = ({ id, multi, router }) => {
    router.push(`${PAGE_URL.SCHEDULING_SUMMARY}/${id}${multi}`)
  }

  const handlePushToSchedulings = ({ router }) => {
    router.push(`${PAGE_URL.SCHEDULING_DIARY}`)
  }

  const formatAddress = (address) => {
    return {
      street: address.street,
      number: address.number,
      zipCode: address.postalCode,
      district: address.neighborhood,
      city: address.city,
      state: address.state,
      country: address.country,
      type: address.addressType
    }
  }

  const payPackage = async ({ router, address, sessionId, creditCard }) => {
    const { card, packageData } = state
    const userBirthDate = window.localStorage.getItem('userBirthDate')
    const serviceId = packageData?.serviceId
    const payload = {
      partnerId: packageData?.partner?.id,
      serviceId: packageData?.service?.id,
      professionalId: packageData?.professional?.id,
      obs: packageData?.obs,
      petId: packageData?.pet?.id,
      additionalServices: packageData?.additionalServices.map(additional => ({ id: additional.id, quantity: additional.quantity })),
      sessionId,
      payment: {
        userBirthDate,
        address,
        creditCard,
        storeCard: card?.storeCard,
        installments: card?.installments.toString()
      }
    }

    if (validatePaymentData(userBirthDate, address, creditCard, packageData)) {
      const paymentResponse = await purchaseAndPayPackage(payload)

      if (paymentResponse.success) {
        if (paymentResponse.data.payments.online.paymentStatus === SCHEDULING_PAY_STATUS.PAYMENT_PRE_AUTHORIZED) {
          showSucessModal({ id: serviceId, multi: false, router, isServicePackage })
        } else if (paymentResponse.data.payments.online.paymentStatus === SCHEDULING_PAY_STATUS.PAYMENT_PENDENT) {
          showPaymentProcessingModal({ router, isServicePackage })
        } else if (paymentResponse.data.payments.online.paymentStatus === SCHEDULING_PAY_STATUS.PAYMENT_NOT_AUTHORIZED) {
          showSchedulingFailureModal({ router, isServicePackage })
        }
      } else {
        console.error('Erro no pagamento', paymentResponse.error)
        showSchedulingFailureModal({ id: serviceId, multi: false, router, isServicePackage })
      }
    } else {
      console.log('Informações insuficientes para realizar o pagamento')
      showSchedulingFailureModal({ id: serviceId, multi: false, router })
    }
  }

  const payScheduling = async ({ router, address, sessionId, creditCard, showLoader, hideLoader }) => {
    const { card, schedulingList } = state
    const userBirthDate = window.localStorage.getItem('userBirthDate')
    const multi = schedulingList.length > 1 ? '?multi=true' : ''
    const payload = {
      userBirthDate,
      address,
      creditCard,
      storeCard: card?.storeCard,
      schedulings: card.schedulings,
      sessionId
    }

    if (validatePaymentData(userBirthDate, address, creditCard, card.schedulings[0])) {
      const paymentResponse = await pay(payload)

      if (paymentResponse.success) {
        if (paymentResponse.data[0].payments.online.paymentStatus === SCHEDULING_PAY_STATUS.PAYMENT_PRE_AUTHORIZED) {
          showSucessModal({ id: schedulingList[0].id, multi, router, isServicePackage })
        } else if (paymentResponse.data[0].payments.online.paymentStatus === SCHEDULING_PAY_STATUS.PAYMENT_PENDENT) {
          showPaymentProcessingModal({ router, isServicePackage })
        } else if (paymentResponse.data[0].payments.online.paymentStatus === SCHEDULING_PAY_STATUS.PAYMENT_NOT_AUTHORIZED) {
          showSchedulingFailureModal({ router, isServicePackage, showLoader, hideLoader })
        }
      } else {
        console.error('Erro no pagamento', paymentResponse.error)
        showSchedulingFailureModal({ id: schedulingList[0].id, multi, router, showLoader, hideLoader })
      }
    } else {
      console.log('Informações insuficientes para realizar o pagamento')
      showSchedulingFailureModal({ id: schedulingList[0].id, multi, router, showLoader, hideLoader })
    }
  }

  const handleConfirmPay = async ({ router, showLoader, hideLoader }) => {
    const { address, card } = state
    const sessionId = window.Konduto?.getVisitorID()
    const formattedAddress = formatAddress(address)
    const creditCard = formatCreditCard(card)

    try {
      if (isServicePackage) {
        await payPackage({ router, address: formattedAddress, sessionId, creditCard })
      } else {
        await payScheduling({
          router,
          address: formattedAddress,
          sessionId,
          creditCard,
          showLoader,
          hideLoader
        })
      }
    } catch (error) {
      console.error('Erro ao tentar realizar o pagamento', error)
      showSchedulingFailureModal({ showLoader, hideLoader })
    }
  }

  const isServicePackage = state?.purchaseType === 'package'

  const getPrice = () => isServicePackage ? state?.packageData?.price : state?.schedulingList[0]?.price
  const getInstallmentId = () => isServicePackage ? state?.packageData?.serviceId : state?.schedulingList[0]?.id
  const getMultiplePets = () => state?.schedulingList?.length > 1

  const functions = {
    confirmAddress,
    updateSchedulings,
    updatePackageData,
    updatePurchaseType,
    updateAcceptTerms,
    storeCreditCard,
    handleConfirmPay,
    getPrice,
    getInstallmentId,
    getMultiplePets,
    showPackageCancelModal
  }

  return (
    <PaymentContext.Provider value={{ state, dispatch, functions }}>
      {children}
    </PaymentContext.Provider>
  )
}

// Hook para acessar o contexto
export function usePaymentContext () {
  const context = useContext(PaymentContext)
  if (!context) {
    throw new Error('usePaymentContext deve ser usado dentro de um Provider!')
  }

  const { functions, state } = context
  return { functions, state }
}
